import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { ErrorBoundary } from 'shared-ui';

import { DodTimeseriesResponse, DodTimeseriesSeries } from 'api/dod/dod.v2.types';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { AppRoute } from 'routing/AppRoute.enum';
import { TkdHeatmapCell } from '../components/tkdHeatmap/TkdHeatmap.types';
import { useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';

import { OverviewTab } from './OverviewTab';
import { OverviewBarChart, OverviewChart, OverviewKpi, OverviewSession } from './OverviewTab.types';
import {
  formatCount,
  formatDurationSeconds,
  formatRate,
  seriesLabel,
  sumSeries,
  toPerMinute,
  toRcpOptions,
  toTkdOptions,
  topSeriesByLastValue,
  dodOutboxStatusLabel,
} from './dodFormat';
import { useOverviewData } from './useOverviewData';

const SPARKLINE_POINTS = 24;
const QUEUE_DEPTH_THRESHOLD = 100;
const TOP_TKD_COUNT = 5;
/** Outbox rows still waiting for delivery; mirrors oldestLiveAge() in the backend overview. */
const OUTBOX_LIVE_STATUSES = ['PENDING', 'CLAIMED', 'DISPATCHED'];
const OUTBOX_AGE_WARN_SECONDS = 300;

/** Last `count` known values of a series, or [] when nothing is known — never a zero line. */
const toSparkline = (points: Array<[number, number | null]>, count = SPARKLINE_POINTS): number[] => {
  const known = points.filter(([, value]) => value !== null && value !== undefined);

  if (known.length === 0) {
    return [];
  }

  return known.slice(-count).map(([, value]) => value as number);
};

/** True when no rollup provider answered — distinct from a provider that answered with nothing. */
const isUnavailable = (response?: DodTimeseriesResponse): boolean => !response || !response.capability;

/**
 * Collapse a timeseries response to one series.
 *
 * An ungrouped query returns a single 'total' series; a grouped one returns many. Summing covers
 * both and keeps the tiles honest when a filter narrows the query to a subset.
 */
const collapse = (response: DodTimeseriesResponse | undefined, key: string): DodTimeseriesSeries => ({
  key,
  labels: {},
  points: !response ? [] : response.series.length === 1 ? response.series[0].points : sumSeries(response.series),
});

export const OverviewTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const filters = useDodFilters();
  const { refreshToken, setToolbarOptions } = useDodOutlet();
  const [isPerTkd, setIsPerTkd] = useState(false);

  const data = useOverviewData({ filters, refreshToken });
  const { overview, stepMs } = data;

  const unknownLabel = formatMessage({ id: AppMessages['dod.overview.unknown'] });

  const kpis = useMemo<OverviewKpi[]>(() => {
    const perMinute = (response: DodTimeseriesResponse | undefined, key: string): Array<[number, number | null]> =>
      response ? toPerMinute(collapse(response, key), stepMs) : [];

    const depthPoints = data.queueDepth.data ? sumSeries(data.queueDepth.data.series) : [];
    const dlqPoints = collapse(data.dlqSize.data, 'dlq').points;

    const oldestOutbox = overview?.oldestOutboxAgeSeconds ?? null;
    // Same statuses the backend's oldestOutboxAgeSeconds is computed from: a terminal row
    // (SUPERSEDED, DELIVERED) may be older, but it is not waiting for anything, and captioning the
    // tile with it contradicted the value next to it.
    const oldestOutboxRow = [...(overview?.outbox ?? [])]
      .filter((row) => row.oldestAgeSeconds !== null && OUTBOX_LIVE_STATUSES.includes(row.status))
      .sort((left, right) => (right.oldestAgeSeconds ?? 0) - (left.oldestAgeSeconds ?? 0))[0];

    /** A delta needs both sides; the backend sends null for either when it cannot compute one. */
    const delta = (value: number | null, previous: number | null): { current: number; previous: number } | undefined =>
      value === null || previous === null ? undefined : { current: value, previous };

    return [
      {
        id: 'queued',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.queued'] }),
        value: overview ? formatCount(overview.queuedTotal) : unknownLabel,
        // The backend sends no previous window for the two totals, so no arrow is drawn.
        goodDirection: 'down',
        tone: 'default',
        sparkline: toSparkline(depthPoints),
      },
      {
        id: 'deadLetter',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.deadLetter'] }),
        value: overview ? formatCount(overview.dlqTotal) : unknownLabel,
        goodDirection: 'down',
        tone: (overview?.dlqTotal ?? 0) > 0 ? 'bad' : 'ok',
        sparkline: toSparkline(dlqPoints),
      },
      {
        id: 'delivered',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.delivered'] }),
        value: formatRate(overview?.deliveredPerMin.value ?? null, unknownLabel),
        delta: delta(overview?.deliveredPerMin.value ?? null, overview?.deliveredPerMin.previous ?? null),
        goodDirection: 'up',
        tone: 'ok',
        sparkline: toSparkline(perMinute(data.delivered.data, 'delivered')),
      },
      {
        id: 'failed',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.failed'] }),
        value: formatRate(overview?.failedPerMin.value ?? null, unknownLabel),
        delta: delta(overview?.failedPerMin.value ?? null, overview?.failedPerMin.previous ?? null),
        goodDirection: 'down',
        tone: (overview?.failedPerMin.value ?? 0) > 0 ? 'bad' : 'ok',
        sparkline: toSparkline(perMinute(data.failed.data, 'failed')),
      },
      {
        id: 'deferred',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.deferred'] }),
        value: formatRate(overview?.deferredPerMin.value ?? null, unknownLabel),
        delta: delta(overview?.deferredPerMin.value ?? null, overview?.deferredPerMin.previous ?? null),
        goodDirection: 'down',
        tone: (overview?.deferredPerMin.value ?? 0) > 0 ? 'warn' : 'default',
        sparkline: toSparkline(perMinute(data.deferred.data, 'deferred')),
        caption: formatMessage({ id: AppMessages['dod.overview.kpi.deferred.caption'] }),
      },
      {
        id: 'oldestOutbox',
        label: formatMessage({ id: AppMessages['dod.overview.kpi.oldestOutbox'] }),
        value: formatDurationSeconds(oldestOutbox, unknownLabel),
        goodDirection: 'down',
        tone: oldestOutbox !== null && oldestOutbox > OUTBOX_AGE_WARN_SECONDS ? 'warn' : 'default',
        sparkline: [],
        caption: oldestOutboxRow
          ? formatMessage(
              { id: AppMessages['dod.overview.kpi.oldestOutbox.caption'] },
              { status: dodOutboxStatusLabel(oldestOutboxRow.status, formatMessage), count: oldestOutboxRow.orders },
            )
          : undefined,
      },
    ];
  }, [
    overview,
    data.delivered.data,
    data.failed.data,
    data.deferred.data,
    data.queueDepth.data,
    data.dlqSize.data,
    stepMs,
    formatMessage,
    unknownLabel,
  ]);

  const sessions = useMemo<OverviewSession[]>(
    () =>
      (overview?.rcp.connections ?? []).map((connection) => ({
        ...connection,
        meta: formatMessage(
          { id: AppMessages['dod.overview.sessions.meta'] },
          {
            circuit: connection.circuit ?? unknownLabel,
            heartbeat:
              connection.heartbeatAgeMs === undefined
                ? unknownLabel
                : formatDurationSeconds(Math.round(connection.heartbeatAgeMs / 1000), unknownLabel),
          },
        ),
      })),
    [overview, formatMessage, unknownLabel],
  );

  /** Worker connection events become vertical markers on the throughput chart. */
  const annotations = useMemo(
    () =>
      (data.worker?.events ?? [])
        .map((event) => ({
          timestamp: Date.parse(event.at),
          label: event.event,
          color: event.event.includes('circuit') ? palette.chart.bad : palette.chart.warn,
        }))
        .filter((annotation) => Number.isFinite(annotation.timestamp) && annotation.timestamp >= data.fromTs),
    [data.worker, data.fromTs, palette],
  );

  const throughput = useMemo<OverviewChart>(() => {
    const parts: Array<{ key: string; response?: DodTimeseriesResponse; color: string }> = [
      { key: 'enqueued', response: data.enqueued.data, color: palette.chart.series[0] },
      { key: 'delivered', response: data.delivered.data, color: palette.chart.ok },
      { key: 'failed', response: data.failed.data, color: palette.chart.bad },
      { key: 'deferred', response: data.deferred.data, color: palette.chart.warn },
    ];

    return {
      title: formatMessage({ id: AppMessages['dod.overview.chart.throughput'] }),
      series: parts.map(({ key, response }) => ({
        key,
        labels: {},
        points: response ? toPerMinute(collapse(response, key), stepMs) : [],
      })),
      seriesLabels: Object.fromEntries(
        parts.map(({ key }) => [
          key,
          formatMessage({ id: AppMessages[`dod.overview.series.${key}` as keyof typeof AppMessages] }),
        ]),
      ),
      seriesColors: Object.fromEntries(parts.map(({ key, color }) => [key, color])),
      area: true,
      stacked: true,
      isLoading: data.enqueued.isLoading || data.delivered.isLoading,
      isUnavailable: isUnavailable(data.enqueued.data) && isUnavailable(data.delivered.data),
      error: data.enqueued.error ?? data.delivered.error ?? data.failed.error ?? data.deferred.error,
      annotations,
    };
  }, [data.enqueued, data.delivered, data.failed, data.deferred, stepMs, palette, formatMessage, annotations]);

  const queueDepth = useMemo<OverviewChart>(() => {
    const response = data.queueDepth.data;
    const all = response?.series ?? [];
    const top = isPerTkd ? topSeriesByLastValue(all, TOP_TKD_COUNT) : [];

    const series: DodTimeseriesSeries[] = isPerTkd ? top : [{ key: 'total', labels: {}, points: sumSeries(all) }];

    return {
      title: formatMessage({ id: AppMessages['dod.overview.chart.queueDepth'] }),
      series,
      seriesLabels: isPerTkd
        ? Object.fromEntries(top.map((one) => [one.key, seriesLabel(one)]))
        : { total: formatMessage({ id: AppMessages['dod.overview.series.total'] }) },
      seriesColors: isPerTkd
        ? Object.fromEntries(
            top.map((one, index) => [one.key, palette.chart.series[index % palette.chart.series.length]]),
          )
        : { total: palette.chart.series[0] },
      area: !isPerTkd,
      isLoading: data.queueDepth.isLoading,
      isUnavailable: isUnavailable(response),
      error: data.queueDepth.error,
      threshold: {
        value: QUEUE_DEPTH_THRESHOLD,
        label: formatMessage({ id: AppMessages['dod.overview.chart.threshold'] }),
        color: palette.chart.warn,
      },
    };
  }, [data.queueDepth, isPerTkd, palette, formatMessage]);

  const deadLetter = useMemo<OverviewChart>(
    () => ({
      title: formatMessage({ id: AppMessages['dod.overview.chart.deadLetter'] }),
      series: [collapse(data.dlqSize.data, 'dlqSize')],
      seriesLabels: { dlqSize: formatMessage({ id: AppMessages['dod.overview.series.dlqSize'] }) },
      seriesColors: { dlqSize: palette.chart.bad },
      area: true,
      isLoading: data.dlqSize.isLoading,
      isUnavailable: isUnavailable(data.dlqSize.data),
      error: data.dlqSize.error,
    }),
    [data.dlqSize, palette, formatMessage],
  );

  /**
   * Moves to the dead-letter queue, split by reason.
   *
   * The rollups carry only rcpId/nlk/command as labels, so `dod_dlq_moved_total` cannot be grouped
   * by reason there. The split comes from GET /dod/dlq/summary, which counts the same moves over
   * the same window.
   */
  const deadLetterByReason = useMemo<OverviewBarChart>(() => {
    const byReason = data.dlqSummary?.byReason ?? [];

    return {
      title: formatMessage({ id: AppMessages['dod.overview.chart.dlqByReason'] }),
      data: byReason.map((entry) => ({
        id: entry.reason,
        label: formatMessage({ id: AppMessages[`dod.common.state.${entry.reason}` as keyof typeof AppMessages] }),
        value: entry.count,
        color: palette.chart.bad,
      })),
      isLoading: data.isDlqSummaryLoading,
      isUnavailable: !!data.dlqSummary && byReason.length === 0,
    };
  }, [data.dlqSummary, palette, formatMessage]);

  const outboxChart = useMemo<OverviewChart>(
    () => ({
      title: formatMessage({ id: AppMessages['dod.overview.chart.outbox'] }),
      series: [collapse(data.outbox.data, 'outbox')],
      seriesLabels: { outbox: formatMessage({ id: AppMessages['dod.overview.series.outbox'] }) },
      seriesColors: { outbox: palette.chart.series[2] },
      area: true,
      isLoading: data.outbox.isLoading,
      isUnavailable: isUnavailable(data.outbox.data),
      error: data.outbox.error,
    }),
    [data.outbox, palette, formatMessage],
  );

  const heatmapCells = useMemo<TkdHeatmapCell[]>(
    () =>
      data.heatmapPages.items.map((row) => ({
        rcpId: row.rcpId,
        rcpNazwa: row.rcpNazwa,
        nlk: row.nlk,
        tkdNazwa: row.tkdNazwa,
        status: row.status,
        depth: row.depth,
        dlqDepth: row.dlqDepth,
      })),
    [data.heatmapPages.items],
  );

  /** The overview already reads a wide page of queues for the heatmap; the toolbar reuses it. */
  useEffect(() => {
    const rows = data.heatmapPages.items;

    setToolbarOptions({ rcp: toRcpOptions(rows), tkd: toTkdOptions(rows) });
  }, [data.heatmapPages.items, setToolbarOptions]);

  /**
   * The heatmap says so when it is showing a subset. The walk has a ceiling, and a grid that
   * quietly stops at the cap would read as "this is the whole fleet".
   */
  const heatmapTitle = data.heatmapPages.isTruncated
    ? formatMessage(
        { id: AppMessages['dod.overview.heatmap.truncated'] },
        { shown: heatmapCells.length, total: data.heatmapPages.total },
      )
    : formatMessage({ id: AppMessages['dod.overview.heatmap.title'] });

  const onSessionClick = useCallback(() => navigate(AppRoute.dodWorker), [navigate]);

  const onHeatmapSelect = useCallback(
    (cell: TkdHeatmapCell) => {
      navigate(`${AppRoute.dodQueues}${filters.queryString}&queue=${cell.rcpId}:${cell.nlk}`);
    },
    [navigate, filters.queryString],
  );

  const onTogglePerTkd = useCallback(() => setIsPerTkd((current) => !current), []);

  return (
    <ErrorBoundary>
      <OverviewTab
        kpis={kpis}
        isKpiLoading={data.isOverviewLoading}
        isError={data.isOverviewError}
        sessions={sessions}
        connectedCount={overview?.rcp.connected ?? 0}
        totalConnections={overview?.rcp.total ?? 0}
        alerts={overview?.alerts ?? []}
        outbox={overview?.outbox ?? []}
        throughput={throughput}
        queueDepth={queueDepth}
        isPerTkd={isPerTkd}
        onTogglePerTkd={onTogglePerTkd}
        deadLetter={deadLetter}
        deadLetterByReason={deadLetterByReason}
        outboxChart={outboxChart}
        heatmapCells={heatmapCells}
        isHeatmapLoading={data.heatmapPages.isLoading}
        heatmapTitle={heatmapTitle}
        onSessionClick={onSessionClick}
        onHeatmapSelect={onHeatmapSelect}
      />
    </ErrorBoundary>
  );
};
